import type { BadgeTone } from '@/components/ui';

/**
 * categoryTheme — fuente única de español + color por slug de "categoría".
 * ROUND15 #6.
 *
 * Combina las tres clases de categorías que aparecen en commerce:
 *  - Productos partners (`partner_products.category`): bebidas / mixologia /
 *    gastronomia / experiencias / reservas / merchandising, etc.
 *  - Experiencias VIP (`experiences.category`): cena / viaje / encuentro.
 *  - Categoría FC del usuario (`user_profiles.tier`): bronze / silver /
 *    gold / platinum / diamond.
 *
 * Cada slug tiene asociado un `label` en español y un `tone` de Badge
 * tomado del set existente (neutral / info / success / warning / danger /
 * gold). Cualquier slug fuera del set conocido cae a `info` con un label
 * derivado por capitalización — así el helper sigue siendo seguro frente
 * a slugs que admin agregue sin avisar.
 */

export type CategoryTheme = {
  label: string;
  tone: BadgeTone;
};

const PARTNER_PRODUCT_THEMES: Record<string, CategoryTheme> = {
  bebidas: { label: 'Bebidas', tone: 'info' },
  mixologia: { label: 'Mixología', tone: 'info' },
  gastronomia: { label: 'Gastronomía', tone: 'gold' },
  experiencias: { label: 'Experiencias', tone: 'success' },
  reservas: { label: 'Reservas', tone: 'warning' },
  merchandising: { label: 'Merchandising', tone: 'neutral' },
  retail: { label: 'Retail', tone: 'neutral' },
  lifestyle: { label: 'Lifestyle', tone: 'gold' },
  servicios: { label: 'Servicios', tone: 'info' },
};

const EXPERIENCE_THEMES: Record<string, CategoryTheme> = {
  cena: { label: 'Cena', tone: 'gold' },
  viaje: { label: 'Viaje', tone: 'info' },
  encuentro: { label: 'Encuentro', tone: 'success' },
};

const LOYALTY_THEMES: Record<string, CategoryTheme> = {
  bronze: { label: 'Bronce', tone: 'warning' },
  silver: { label: 'Plata', tone: 'neutral' },
  gold: { label: 'Oro', tone: 'gold' },
  platinum: { label: 'Platino', tone: 'info' },
  diamond: { label: 'Diamante', tone: 'success' },
};

function fallbackTheme(slug: string): CategoryTheme {
  const trimmed = slug.trim();
  if (!trimmed) return { label: '—', tone: 'neutral' };
  const label = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  return { label, tone: 'info' };
}

/**
 * Devuelve label + tone para cualquier slug de categoría conocido.
 *
 * El orden de búsqueda es: partner products → experiencias → categoría FC
 * (loyalty) → fallback genérico. Los slugs no colisionan entre namespaces
 * en este momento.
 */
export function categoryTheme(slug: string | null | undefined): CategoryTheme {
  if (!slug) return fallbackTheme('');
  const key = slug.trim().toLowerCase();
  return (
    PARTNER_PRODUCT_THEMES[key] ??
    EXPERIENCE_THEMES[key] ??
    LOYALTY_THEMES[key] ??
    fallbackTheme(key)
  );
}

/** Atajo para casos que sólo quieren el label visible. */
export function categoryLabel(slug: string | null | undefined): string {
  return categoryTheme(slug).label;
}
